import React, { useState, useEffect } from "react";
import "../styles/main.css";
import "../styles/variables.css";
import { logoutUser } from "../core/store/actions/auth";
import { useAuth } from "../core/AuthContext"; // Import the useAuth hook
import { useSemester } from "../core/SemesterContext";
import { connect } from "react-redux";
import { Icon } from "@iconify/react";
import axios from "axios";

import { Link } from "react-router-dom";
import { URL } from "../core/config";
import { getDepartmentIcon, getDepartmentColor, getDepartmentId, getDepartmentName } from "../core/constants/departments";
import { useDepartments } from "../core/DepartmentsContext";

import whatsapp from "./images/whatsapp.png";
import drive from "./images/googledrive.png";
import esn from "./images/esn.png";
import linktree from "./images/linktree.png";
import tiktok from "./images/tiktok.png";
import instagram from "./images/instagram.png";
import taiga_colors from "./images/taiga_colors.png";
import teamup from "./images/teamup.png";
import taiga_grey from "./images/taiga_grey.png";
import CountUp from "react-countup";
import ContactNetwork from "./ContactNetwork";
import NavHeader from "./NavHeader";
import useUsers from "./hooks/useUsers";
import WarningMessage from "./WarningMessage";
import Avatar from "./Avatar";

const eventsURL = URL + "/events";

const tintColor = (hex, alpha) => {
  if (!hex) return `rgba(107, 114, 128, ${alpha})`;
  const parsed = hex.replace("#", "");
  const r = parseInt(parsed.substring(0, 2), 16);
  const g = parseInt(parsed.substring(2, 4), 16);
  const b = parseInt(parsed.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function Main({ user, isAuthenticated, logoutUser }) {
  // Use the AuthContext to access user information
  const authContext = useAuth();
  const contextUser = authContext.user;
  const contextIsAuthenticated = authContext.isAuthenticated;

  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;
  const { departments: catalog } = useDepartments();

  const { users } = useUsers();

  // Log both Redux and Context user data for debugging
  //console.log('User object in Main component (Redux):', user);
  //console.log('User object in Main component (Context):', contextUser);

  const isPastThisWeek = (eventDate) => {
    const today = new Date();
    // Remove time part for accurate comparison
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  const isToday = (eventDate) => {
    const today = new Date();
    return (
      eventDate.getDate() === today.getDate() &&
      eventDate.getMonth() === today.getMonth() &&
      eventDate.getFullYear() === today.getFullYear()
    );
  };

  const [eventsThisWeek, setEventsThisWeek] = useState([]);
  const [userStats, setUserStats] = useState({
    eventsAsMO: 0,
    eventsAsVMO: 0,
    eventsAsStaff: 0,
    totalEvents: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Get the current user ID from either Redux or Context
  const currentUserId = user?._id || contextUser?._id;

  useEffect(() => {
    let isMounted = true;

    const fetchUserEvents = async () => {
      if (!currentUserId || !semester?._id) {
        if (isMounted) setStatsLoading(false);
        return;
      }

      try {
        const eventsResponse = await axios.get(
          `${eventsURL}/eventsbyuser/${currentUserId}?semester=${semester._id}`,
        );

        if (!isMounted) return;

        const eventsData = eventsResponse.data;

        let eventsAsMO = 0;
        let eventsAsVMO = 0;
        let eventsAsStaff = 0;

        for (const event of eventsData) {
          if (event.mos && event.mos.includes(currentUserId)) {
            eventsAsMO++;
          }
          if (event.vmos && event.vmos.includes(currentUserId)) {
            eventsAsVMO++;
          }
          if (
            event.participants &&
            event.participants.includes(currentUserId)
          ) {
            eventsAsStaff++;
          }
        }

        const totalEvents = eventsAsMO + eventsAsVMO + eventsAsStaff;

        if (isMounted) {
          setUserStats({
            eventsAsMO,
            eventsAsVMO,
            eventsAsStaff,
            totalEvents,
          });

          setStatsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching user events:", error);
        if (isMounted) setStatsLoading(false);
      }
    };

    fetchUserEvents();

    return () => {
      isMounted = false;
    };
  }, [currentUserId, semester?._id]);

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      if (!semester?._id) {
        return;
      }

      try {
        const response = await axios.get(eventsURL + "/list", { params: { semester: semester._id } });

        if (!isMounted) return;

        const events = response.data;

        const today = new Date();
        const startOfWeek = new Date(
          today.setDate(today.getDate() - today.getDay()),
        ); // Start of the current week (Sunday)
        const endOfWeek = new Date(today.setDate(today.getDate() + 7)); // End of the current week (Saturday)

        // console.log("Events: ", events)
        // Filter events to get only those happening this week
        const filteredEvents = events.filter((event) => {
          const eventDate = new Date(event.date);
          return eventDate >= startOfWeek && eventDate <= endOfWeek;
        });

        // Today's events first, then upcoming, then past events at the end
        const rankEvent = (event) => {
          const eventDate = new Date(event.date);
          if (isToday(eventDate)) return 0;
          if (isPastThisWeek(eventDate)) return 2;
          return 1;
        };

        const sortedEvents = [...filteredEvents].sort((a, b) => {
          const rankDiff = rankEvent(a) - rankEvent(b);
          if (rankDiff !== 0) return rankDiff;
          return new Date(a.date) - new Date(b.date);
        });

        if (isMounted) {
          setEventsThisWeek(sortedEvents);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [semester?._id]); // Only run once when the component mounts

  // Check authentication status
  if (!isAuthenticated && !contextIsAuthenticated) {
    return <div>Please log in to access this page.</div>;
  }

  function Counter({ number, title, icon, color }) {
    return (
      <div className="stat-card">
        <div
          className="stat-icon"
          style={{ background: tintColor(color, 0.15), color }}
        >
          {icon}
        </div>
        <div className="stat-body">
          <CountUp duration={10} className="counter" end={number} />
          <span>{title}</span>
        </div>
      </div>
    );
  }

  const nextBirthdays = Array.isArray(users)
    ? (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Start of week (Monday) / end of week (Sunday) for the "this week" badge
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(startOfWeek.getDate() + diff);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        return users
          .filter((u) => u.birthDate)
          .map((u) => {
            const original = new Date(u.birthDate);
            const nextDate = new Date(original);
            nextDate.setFullYear(today.getFullYear());
            nextDate.setHours(0, 0, 0, 0);
            if (nextDate < today) {
              nextDate.setFullYear(today.getFullYear() + 1);
            }
            return {
              _id: u._id,
              name: u.nickname || u.firstLastName || u.fullName,
              fullName: u.fullName,
              profilePictureUrl: u.profilePictureUrl,
              date: nextDate,
              department: u.departments?.[0],
              isThisWeek: nextDate >= startOfWeek && nextDate <= endOfWeek,
              isBirthdayToday: nextDate.getTime() === today.getTime(),
            };
          })
          .sort((a, b) => a.date - b.date)
          .slice(0, 10);
      })()
    : [];

  return (
    <div className="page-container">
      <NavHeader />
      <div className="title">
        Hey,{" "}
        {user && user.name
          ? user.name
          : contextUser && contextUser.name
            ? contextUser.name
            : "Guest"}
        !
      </div>

      {/* Warning to inform user to add his missing ESN email to Junior members*/}
      {(!user?.institutionalEmail?.includes("@esnlisboa.org") || !contextUser?.institutionalEmail?.includes("@esnlisboa.org"))
      && (user?.membershipType === "Junior" || user?.membershipType === "Senior" || contextUser?.membershipType === "Junior" || contextUser?.membershipType === "Senior")
      && (
        <WarningMessage
          message={
            <>
              As a <i>{user?.membershipType || contextUser?.membershipType}</i> member, please complete your profile by adding your ESN email account.
            </>
          }
          userId={currentUserId}
        />
      )}

      {/* Warning to inform user to add a short name */}
      {(!user?.firstLastName && !contextUser?.firstLastName) && (
        <WarningMessage
          message="Please complete your profile by adding your first and last name."
          userId={currentUserId}
        />
      )}

      {/* {(!user?.personalEmailVerification?.isVerified || (user?.institutionalEmail && !user?.institutionalEmailVerification?.isVerified) || !contextUser?.personalEmailVerification?.isVerified || (contextUser?.institutionalEmail && !contextUser?.institutionalEmailVerification?.isVerified)) && (
        <WarningMessage
          message="Please verify your email addresses."
          userId={currentUserId}
        />
      )} */}

      {/* Warning to inform user to add their ESN account username */}
      {(!user?.esnAccountUsername && !contextUser?.esnAccountUsername) && (
        <WarningMessage
          message="Please complete your profile by adding your ESN account username."
          userId={currentUserId}
        />
      )}

      <div className="counters-container">
        <h3 className="main-section-title">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: tintColor("#8b5e3c", 0.15), color: "#8b5e3c" }}
            >
              <Icon icon="line-md:coffee-half-empty-filled-loop" className="icon" />
            </span>
          </span>
          Your semester stats:
        </h3>
        <div className="counters">
          {statsLoading ? (
            <>
              <div className="stat-card skeleton" />
              <div className="stat-card skeleton" />
              <div className="stat-card skeleton" />
              <div className="stat-card skeleton" />
            </>
          ) : (
            <>
              <Counter
                number={userStats.eventsAsMO}
                title="as MO"
                icon={<Icon icon="fluent-mdl2:party-leader" />}
                color="#ec008c"
              />
              <Counter
                number={userStats.eventsAsVMO}
                title="as VMO"
                icon={<Icon icon="carbon:events" />}
                color="#00aeef"
              />
              <Counter
                number={userStats.eventsAsStaff}
                title="as Staff"
                icon={<Icon icon="mdi:account-group" />}
                color="#7ac143"
              />
              <Counter
                number={
                  userStats.eventsAsMO +
                  userStats.eventsAsVMO +
                  userStats.eventsAsStaff
                }
                title="Total"
                icon={<Icon icon="ant-design:number-outlined" />}
                color="#f47b20"
              />
            </>
          )}
        </div>
      </div>

      <div className="main-phrase">
        Sardines on FIRE, Lisbon Is Terrifying
      </div>

      {semester && semester.isActive && (
        <div className="main-week-structure" style={{ margin: "50px 0" }}>
          <h3 className="main-section-title">
            <span className="main-section-icon">
              <span
                className="main-section-icon-fill"
                style={{ background: tintColor("#00aeef", 0.15), color: "#00aeef" }}
              >
                <Icon icon="line-md:calendar" className="icon" />
              </span>
            </span>
            Events of the Week
          </h3>
          <div className="events-this-week">
            {eventsThisWeek.length > 0 ? (
              eventsThisWeek.map((event) => {
                const eventDate = new Date(event.date);
                const formattedDate = eventDate.toLocaleDateString();
                const formattedTime = eventDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const isEventToday = isToday(eventDate);
                const isEventPastThisWeek = isPastThisWeek(eventDate);
                const departments = event.departments?.length
                  ? event.departments
                  : [];

                let eventClass = "main-week-card";
                if (isEventToday) {
                  eventClass += " highlight-today";
                } else if (isEventPastThisWeek) {
                  eventClass += " past-this-week";
                }
                return (
                  <div key={event._id} className={eventClass}>
                    {isEventToday && (
                      <div className="main-week-today-badge">Today</div>
                    )}
                    <div className="main-week-banner">
                      {departments.length > 0 ? (
                        departments.map((dept) => {
                          const deptColor = getDepartmentColor(dept, catalog) || "#6b7280";
                          const deptKey = getDepartmentId(dept) || getDepartmentName(dept);
                          return (
                            <div
                              key={deptKey}
                              className="main-week-banner-segment"
                              style={{ backgroundColor: tintColor(deptColor, 0.15) }}
                            >
                              {getDepartmentIcon(dept, {
                                className: "main-week-banner-icon",
                                style: { color: deptColor },
                              }, catalog)}
                            </div>
                          );
                        })
                      ) : (
                        <div className="main-week-banner-segment" />
                      )}
                    </div>
                    <div className="main-week-info">
                      <div className="main-week-title">
                        {event.name || "Untitled Event"}
                      </div>
                      <div className="main-week-date">{`${formattedDate} at ${formattedTime}`}</div>
                      {event.meetingPoint && (
                        <div className="main-week-location">
                          {event.meetingPoint}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p>No events this week.</p>
            )}
          </div>
        </div>
      )}

      <div className="main-week-structure" style={{ margin: "50px 0" }}>
        <h3 className="main-section-title">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: tintColor("#ec008c", 0.15), color: "#ec008c" }}
            >
              <Icon icon="line-md:cake" className="icon" />
            </span>
          </span>
          Upcoming Birthdays
        </h3>
        <div className="birthday-week-strip">
          {nextBirthdays.length > 0 ? (
            nextBirthdays.map((b) => {
              const dateLabel = b.date.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
              });
              const deptColor = getDepartmentColor(b.department, catalog) || "#6b7280";
              return (
                <Link
                  key={b._id}
                  to={`/users/${b._id}`}
                  className={`birthday-card${b.isBirthdayToday ? " birthday-today" : ""}`}
                >
                  {b.isBirthdayToday && (
                    <div className="birthday-today-badge">
                      <Icon icon="line-md:cake" /> Today
                    </div>
                  )}
                  {b.isThisWeek && (
                    <div className="birthday-day">
                      {b.date.toLocaleDateString("en-US", { weekday: "long" })}
                    </div>
                  )}
                  <div className="birthday-date">{dateLabel}</div>
                  <div className="birthday-avatar-circle">
                    <Avatar user={b} size={48} />
                  </div>
                  <div className="birthday-name">{b.name}</div>
                  <div
                    className="birthday-department-row"
                    style={{
                      backgroundColor: tintColor(deptColor, 0.15),
                      borderColor: tintColor(deptColor, 0.35),
                    }}
                  >
                    <div className="birthday-icon" style={{ color: deptColor }}>
                      {getDepartmentIcon(b.department, {}, catalog)}
                    </div>
                    <div className="department">{getDepartmentName(b.department)}</div>
                  </div>
                </Link>
              );
            })
          ) : (
            <div className="no-birthdays">
              <Icon icon="tabler:fish" className="icon_fish" />
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="main-section-title">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: tintColor("#f47b20", 0.15), color: "#f47b20" }}
            >
              <Icon icon="line-md:link" className="icon" />
            </span>
          </span>
          Useful links
        </h3>
        <div className="links-container useful-links-list">
          <a
            className="links-element"
            href="https://teamup.com/ksfkmoscvvcta6wvzr"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={teamup} alt="Teamup" className="links-icon"></img>
            </div>
            <div className="links-title">Events Teamup</div>
            <div className="links-description">Events Calendar</div>
          </a>
          <a
            className="links-element"
            href="https://eventupp.eu/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={esn} alt="Taiga" className="links-icon"></img>
            </div>
            <div className="links-title">EventUpp</div>
            <div className="links-description">(aka Portal) Publish events</div>
          </a>
          <a
            className="links-element"
            href="https://tree.taiga.io/login"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={taiga_colors} alt="Taiga" className="links-icon"></img>
            </div>
            <div className="links-title">Taiga</div>
            <div className="links-description">Plan & Organize Events</div>
          </a>
          <a
            className="links-element"
            href="https://activities.esn.org/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={esn} alt="ESN Activities" className="links-icon"></img>
            </div>
            <div className="links-title">ESN Activities</div>
            <div className="links-description">
              Activities for the Erasmus Generation
            </div>
          </a>
          <a
            className="links-element"
            href="https://teamup.com/ksoz93rr15f2ch1e3g"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={teamup} alt="Teamup" className="links-icon"></img>
            </div>
            <div className="links-title">Office Teamup</div>
            <div className="links-description">Office Calendar</div>
          </a>
          <a
            className="links-element"
            href="https://drive.google.com/drive/u/1/folders/0AAmk9bCDRAolUk9PVA"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={drive} alt="drive" className="links-icon"></img>
            </div>
            <div className="links-title">ESN Drive</div>
            <div className="links-description">Everything is here</div>
          </a>
          <a
            className="links-element"
            href="https://www.esnlisboa.org/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={esn} alt="esn" className="links-icon"></img>
            </div>
            <div className="links-title">ESN Website</div>
            <div className="links-description">
              Everything about ESN & our stakeholders
            </div>
          </a>
          <a
            className="links-element"
            href="https://forms.gle/Ujvv33kvGnDLbgwNA"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={esn} alt="esn" className="links-icon"></img>
            </div>
            <div className="links-title">Reimbursement</div>
            <div className="links-description">
              Fill out your reimbursement here
            </div>
          </a>
          <a
            className="links-element"
            href="https://forms.gle/6bD4s1NtFqHSbQnG8"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={esn} alt="esn" className="links-icon"></img>
            </div>
            <div className="links-title">IBAN</div>
            <div className="links-description">
              Fill out your IBAN here
            </div>
          </a>
          <a
            className="links-element"
            href="https://www.instagram.com/esnlisboa/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={instagram} alt="instagram" className="links-icon"></img>
            </div>
            <div className="links-title">Instagram</div>
            <div className="links-description">
              Our main social media platform
            </div>
          </a>
          <a
            className="links-element"
            href="https://web.whatsapp.com/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={whatsapp} alt="whatsapp" className="links-icon"></img>
            </div>
            <div className="links-title">Whatsapp</div>
            <div className="links-description">
              SOS communication & Erasmus groups
            </div>
          </a>
          <a
            className="links-element"
            href="https://www.tiktok.com/@esnlisboa"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={tiktok} alt="tiktok" className="links-icon"></img>
            </div>
            <div className="links-title">Tiktok</div>
            <div className="links-description">
              Showcase of ESN Lisboa's coolness
            </div>
          </a>
          <a
            className="links-element"
            href="https://linktr.ee/ESNLisboa"
            target="_blank"
            rel="noreferrer"
          >
            <div className="links-icon-wrapper">
              <img src={linktree} alt="linktree" className="links-icon"></img>
            </div>
            <div className="links-title">Linktree</div>
            <div className="links-description">
              Useful links for the Erasmus
            </div>
          </a>
        </div>
      </div>

      <div style={{ margin: "50px 0" }}>
        <h3 className="main-section-title">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: tintColor("#7ac143", 0.15), color: "#7ac143" }}
            >
              <Icon icon="bx:archive" className="icon" />
            </span>
          </span>
          Useful Documents
        </h3>
        <div className="links-container useful-docs-list">
          <a
            className="docs-element"
            href="https://drive.google.com/file/d/1BNurQi0KJ1qRpwqn1nk-lfQ5Jkc2AmaN/view?usp=drive_link"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <i class="uil uil-question docs-icon"></i>
            </div>
            <div className="docs-title">ESN 101</div>
            <div className="docs-description">The ESN holy book</div>
          </a>
          <a
            className="docs-element"
            href="https://docs.google.com/file/d/1bq453EYS4xf-JJD8Hi80kWHZiULEhmmk/view?usp=drive_link"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <i class="uil uil-file-bookmark-alt docs-icon"></i>
            </div>
            <div className="docs-title">Regulamento Interno</div>
            <div className="docs-description">That's how we roll</div>
          </a>
          <a
            className="docs-element"
            href="https://drive.google.com/file/d/1pHGttIgWyE97FoEnbdj_HmgTxZv8amE_/view?usp=drive_link"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <i class="uil uil-file-bookmark-alt docs-icon"></i>
            </div>
            <div className="docs-title">Estatutos</div>
            <div className="docs-description">That's how we roll</div>
          </a>
          <a
            className="docs-element"
            href="https://drive.google.com/file/d/1nidFTrZLbDDNI9ImbOZ4T9h0qfz7tkrU/view?usp=drive_link"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <i class="uil uil-file-bookmark-alt docs-icon"></i>
            </div>
            <div className="docs-title">Código de Conduta</div>
            <div className="docs-description">That's how we roll</div>
          </a>
          <a
            className="docs-element"
            href="https://wiki.esn.org/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <Icon icon="tabler:book" className="docs-icon" />
            </div>
            <div className="docs-title">ESN Wiki</div>
            <div className="docs-description">
              STrips, ICE, and other abbreviations
            </div>
          </a>
          <a
            className="docs-element"
            href="https://drive.google.com/drive/u/4/folders/1Ot5p56xS1droa5cy1QiBWnoRWygKDbkR"
            target="_blank"
            rel="noreferrer"
          >
            <div className="docs-icon-wrapper">
              <img src={taiga_grey} alt="" width={38} height={38} />
            </div>
            <div className="docs-title">Taiga Tutorials</div>
            <div className="docs-description">
              Confused with Taiga? We got you
            </div>
          </a>
        </div>
      </div>

      <div style={{ margin: "50px 0" }}>
        <h3 className="main-section-title">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: tintColor("#2e3192", 0.15), color: "#2e3192" }}
            >
              <Icon icon="majesticons:fish-line" className="icon" />
            </span>
          </span>
          Contact Network
        </h3>
        <ContactNetwork />
      </div>

    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user.profile,
  isAuthenticated: state.user.isAuthenticated,
});

const mapDispatchToProps = { logoutUser };

export default connect(mapStateToProps, mapDispatchToProps)(Main);
